import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';

import { createTrustAccount, updateTrustAccount } from '../../../api/firmsApi/trustAccountsApi';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import { firmTrustAccountFormSchema } from '../../../helpers/formSchemas/formSchemas';
import { FIRM_TRUST_ACCOUNT_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import useSnackBar from '../../../hooks/useSnackBar';
import { TrustAccountShort } from '../../../types';
import { TrustAccountSubmitFormProps } from '../types';

import FirmTrustAccountForm from './FirmTrustAccountForm';

const useStyles = makeStyles()((theme: Theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.light,
      maxWidth: '800px',
      width: '100%',
      padding: '32px',
      boxSizing: 'border-box',
      borderRadius: '16px',
    },

    '& .MuiDialogTitle-root': {
      padding: 0,
      fontSize: '34px',
      fontWeight: 600,
      lineHeight: '120%',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },

    '& .MuiDialogContent-root': {
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
    },

    '& .MuiDialogContentText-root': {
      color: theme.palette.secondary.main,
    },
  },
  popupActions: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '416px',
    width: '100%',
    margin: '0 auto',
    gap: '16px',
    padding: 0,
  },
  popupButton: {
    color: theme.palette.info.main,

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
    },

    '&:hover': {
      background: 'rgba(144, 202, 249, 0.08)',
    },
  },
}));

export enum Mode {
  Create = 'create',
  Edit = 'edit',
}

interface FirmTrustAccountPopupProps {
  isOpen: boolean;
  mode: Mode;
  data?: TrustAccountShort | null;
  loading?: boolean;
  canEdit?: boolean;
  onSuccess?: () => void;
  onFailed?: () => void;
  onCancel: () => void;
}

const FirmTrustAccountPopup = ({
  isOpen,
  mode,
  data,
  loading,
  canEdit,
  onSuccess,
  onFailed,
  onCancel,
}: FirmTrustAccountPopupProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();

  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(firmTrustAccountFormSchema),
    defaultValues: FIRM_TRUST_ACCOUNT_FORM_DEFAULT_VALUES,
  });

  const {
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
  } = formMethods;

  const handleCreateTrustAccount = (submitData: TrustAccountSubmitFormProps) => {
    setIsFormSubmitting(true);

    createTrustAccount(submitData)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Trust account created successfully.',
          type: 'success',
        }));
        onSuccess?.();
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message,
          type: 'error',
        }));
        onFailed?.();
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  const handleUpdateTrustAccount = (submitData: TrustAccountSubmitFormProps) => {
    if (!data) {
      return;
    }

    setIsFormSubmitting(true);

    const payload: Partial<TrustAccountSubmitFormProps> = {
      ...(data.name !== submitData.name && { name: submitData.name }),
      ...(data.type !== submitData.type && { type: submitData.type }),
      ...(data.accountNumber !== submitData.accountNumber && {
        accountNumber: submitData.accountNumber,
      }),
      ...(data.routingNumber !== submitData.routingNumber && {
        routingNumber: submitData.routingNumber,
      }),
    };

    updateTrustAccount(data.id, payload)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Trust account updated successfully.',
          type: 'success',
        }));
        onSuccess?.();
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message,
          type: 'error',
        }));
        onFailed?.();
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  const onSubmitForm = (values: TrustAccountSubmitFormProps) => {
    switch (mode) {
      case 'create':
        handleCreateTrustAccount(values);
        return;
      case 'edit':
        handleUpdateTrustAccount(values);
        return;
      default:
        return;
    }
  };

  const handleClose = () => {
    onCancel();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (mode === 'create') {
      reset(FIRM_TRUST_ACCOUNT_FORM_DEFAULT_VALUES);
      return;
    }

    if (mode === 'edit' && data) {
      reset({
        name: data.name,
        type: data.type,
        accountNumber: data.accountNumber,
        routingNumber: data.routingNumber,
      });
    }
  }, [isOpen, mode, data]);

  return (
    <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose}>
      <Box display="flex" justifyContent="flex-end">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disableRipple
          sx={(theme) => ({
            color: theme.palette.common.white,
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogTitle>{mode === 'create' ? 'Add Trust Account' : 'Edit Trust Account'}</DialogTitle>
      {loading ? (
        <Loader colorType={'warning'} />
      ) : (
        <>
          <DialogContent>
            <FormProvider {...formMethods}>
              <FirmTrustAccountForm
                mode={mode}
                canUpdatePassportInfo={data?.canUpdatePassportInfo}
                canEdit={canEdit}
              />
            </FormProvider>
          </DialogContent>
          {(mode === Mode.Create || canEdit) && (
            <DialogActions className={classes.popupActions}>
              <FormSubmitButton
                disabled={!isValid || !isDirty || isFormSubmitting}
                buttonText="Submit"
                onClick={handleSubmit(onSubmitForm)}
              />
              <FormCancelButton onClick={handleClose} />
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
};

export default FirmTrustAccountPopup;
